import * as React from 'react';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import { SERVER_URL } from 'Constants';
import { store } from 'Models/Store';
import queryString from 'querystring';
import { clearFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { clearBookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import { clearBookingPricingCache } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardPricingCacheStore';

/**
 * Derives the Knowledge Base logout URL from the configured autologin link.
 *
 * The configured link is the autologin variant (e.g. `.../tenant/x/login?autologin`).
 * We reuse it to build the sign-out route (`.../tenant/x/logout`) and tell the
 * Knowledge Base to send the browser back to FerryHawk once it has cleared its
 * session. Returns undefined when no Knowledge Base is configured.
 */
function buildKnowledgeBaseLogoutUrl(finalRedirect: string): string | undefined {
	const knowledgeBaseLink = store.configuration?.knowledgeBaseLink;
	if (!knowledgeBaseLink) {
		return undefined;
	}
	try {
		const url = new URL(knowledgeBaseLink);
		url.search = '';
		url.pathname = url.pathname.replace(/\/login\/?$/, '/logout');
		url.searchParams.set('redirect', finalRedirect);
		return url.toString();
	} catch {
		return undefined;
	}
}

/**
 * Logs the user out of the application
 *
 * Can take a location query param that will redirect after the logout occurs.
 *
 * For staff+ users we also clear the FerryHawk Knowledge Base session. Only
 * staff+ ever have a Knowledge Base session, and a stale one can otherwise leave
 * them viewing the Knowledge Base as a previously signed-in user. We log out of
 * FerryHawk first (so logout always succeeds even if the Knowledge Base is
 * unreachable), then hop through the Knowledge Base sign-out, then land on the
 * original redirect target.
 */
const Logout = observer(() => {
	const location = useLocation();

	React.useEffect(() => {
		const redirect = queryString.parse(location.search.substring(1)).redirect ?? undefined;
		// This will clear the new and old booking wizard data (if any)
		clearBookingWizardData();
		clearBookingPricingCache();
		clearFerryBookingTransactionIdFromStorage();

		const finalRedirect = `${redirect ? `${redirect}` : '/'}`;

		// For staff+, chain the FerryHawk logout into the Knowledge Base logout so a
		// single sign-out clears both sessions. Skipped in development, where the
		// Knowledge Base typically isn't running and the extra hop would fail.
		const chainKnowledgeBaseLogout = store.isStaff && process.env.NODE_ENV !== 'development';
		const kbLogoutUrl = chainKnowledgeBaseLogout
			? buildKnowledgeBaseLogoutUrl(`${window.location.origin}${finalRedirect}`)
			: undefined;
		const postLogoutRedirect = kbLogoutUrl ?? finalRedirect;

		window.location.href = `${SERVER_URL}/api/authorization/logout?redirect=${encodeURIComponent(postLogoutRedirect)}`;
	}, [location.search]);

	return null;
});

export default Logout;
